import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { setUser as setSentryUser } from '@/lib/sentry';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Admin identity comes from `app_metadata.is_admin`, which is settable only via
 * Supabase's admin API and is signed into the JWT — unlike `user_metadata`, a
 * user cannot grant it to themselves. The database reads the same claim in
 * `public.is_admin()` for RLS, so the UI gate and the data gate agree.
 *
 * This has to be checked on every session, not just at login: this project's
 * Supabase instance is shared with the brand dashboard, so a plain
 * `!!session` test would let any brand user's token render the admin console.
 * RLS would still refuse their queries, but the console also drives surfaces
 * that RLS doesn't cover (the pipeline server, the intel worker).
 */
function isAdminSession(session: Session | null): boolean {
  return session?.user?.app_metadata?.is_admin === true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // A signed-in non-admin is signed back out rather than just being denied,
    // so a stale non-admin session can't sit in storage being retried.
    const apply = (session: Session | null) => {
      const admin = isAdminSession(session);
      if (session && !admin) void supabase.auth.signOut();
      setIsAuthenticated(admin);
      setSentryUser(admin && session?.user ? { id: session.user.id, email: session.user.email } : null);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      apply(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      apply(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    if (!data.user?.app_metadata?.is_admin) {
      await supabase.auth.signOut();
      return { success: false, error: 'Access denied. This account does not have admin privileges.' };
    }
    return { success: true };
  };

  const logout = () => {
    supabase.auth.signOut();
  };

  if (loading) return null;

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
